import SaveBar from './SaveBar';

// Placeholder data for the interim (Markup concierge) one-pager stub (BPP-82).
// Real values will come from the interim generation flow in a follow-up ticket.
const STUB = {
  fullName: 'Jane Doe',
  firstName: 'Jane',
  tags: ['Available Immediately', 'Open to Travel'],
  generalBio: [
    'Jane is a seasoned interim CFO with 20+ years of experience leading finance organizations at PE-backed, middle-market companies. She has served in interim and fractional capacities across six engagements, specializing in carve-outs, post-acquisition integration, and finance function stand-ups.',
    'Most recently, Jane served as interim CFO for a $150M revenue industrial services platform, where she rebuilt the monthly close process, implemented a 13-week cash flow forecast, and supported the sponsor through a successful add-on acquisition.',
  ],
  whyWeRecommend: [
    'Jane brings the exact combination of hands-on finance leadership and PE reporting cadence your situation calls for. Her track record standing up finance functions in newly carved-out businesses maps directly to the immediate needs of this engagement.',
    'References consistently highlight her ability to establish credibility with lenders and sponsors within the first 30 days while stabilizing day-to-day finance operations.',
  ],
  availability: {
    start: 'Available to start within 2 weeks',
    commitment: 'Full-time, on-site up to 4 days/week',
    duration: 'Open to 3–9 month engagements',
  },
  engagement: {
    fees: '$XXX/hour — final rate confirmed at engagement',
    resumeLabel: 'View Resume',
  },
};

// BPP-134: resume is served from blob storage; no fallback link when the account has none.

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <p
      className="font-['Hanken_Grotesk',sans-serif] text-[24px] font-bold leading-[36px] tracking-[0.75px] text-[#132543]"
      style={{ fontVariationSettings: "'wdth' 100" }}
    >
      {children}
    </p>
  );
}

function SectionBody({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="mt-[16px] space-y-[12px] font-['Open_Sans',sans-serif] text-[16px] font-normal leading-[22.8px] text-[#31394C]"
      style={{ fontVariationSettings: "'wdth' 100" }}
    >
      {children}
    </div>
  );
}

export default function InterimOnePager({
  tagline,
  location,
  linkedInUrl,
  resumeUrl,
}: {
  tagline?: string;
  location?: string;
  linkedInUrl?: string;
  resumeUrl?: string | null;
}) {
  const sourcedTags = [tagline, location].filter((tag): tag is string => Boolean(tag));

  return (
    <div className="relative">
      <div className="mx-auto w-full" data-pdf-exclude>
        <div className="bg-white p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="rounded-full border border-[#364153] bg-gray-100 px-3 py-1 text-sm font-semibold text-[#364153]">
              Draft
            </div>
            <div className="rounded-full bg-blue-100 px-3 py-1 text-sm font-semibold text-blue-800">
              Interim One-Pager (Preview)
            </div>
          </div>
        </div>
      </div>

      {/* Banner with name and tags */}
      <div className="w-full">
        <div className="relative h-[159px] w-full overflow-hidden rounded-[12px] border border-[#c1c1c1]">
          <div className="absolute inset-0 bg-[#132543]" />
          <div className="absolute inset-0 flex flex-col justify-center px-[32px]">
            <div className="flex items-center gap-[12px]">
              <p
                className="font-['Hanken_Grotesk',sans-serif] text-[32px] font-bold leading-[40px] text-white"
                style={{ fontVariationSettings: "'wdth' 100", letterSpacing: '0.288px' }}
              >
                {STUB.fullName}
              </p>
              {linkedInUrl && (
                <a
                  href={linkedInUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="font-['Inter',sans-serif] text-[14px] font-medium leading-[20px] text-white underline"
                >
                  LinkedIn
                </a>
              )}
            </div>
            <div className="mt-[12px] flex flex-wrap gap-[8px]">
              {[...sourcedTags, ...STUB.tags].map((tag) => (
                <span
                  key={tag}
                  className="rounded-full bg-white/15 px-[12px] py-[4px] font-['Inter',sans-serif] text-[13px] font-medium leading-[18px] text-white"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-[40px] space-y-[48px] pb-[88px]">
        <section id="section-general-bio">
          <SectionTitle>General Bio</SectionTitle>
          <SectionBody>
            {STUB.generalBio.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </SectionBody>
        </section>

        <section id="section-why-we-recommend">
          <SectionTitle>{`Why we recommend ${STUB.firstName}`}</SectionTitle>
          <SectionBody>
            {STUB.whyWeRecommend.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </SectionBody>
        </section>

        <section id="section-availability">
          <SectionTitle>Availability</SectionTitle>
          <SectionBody>
            <ul className="list-disc space-y-[8px] pl-[20px]">
              <li>{STUB.availability.start}</li>
              <li>{STUB.availability.commitment}</li>
              <li>{STUB.availability.duration}</li>
            </ul>
          </SectionBody>
        </section>

        <section id="section-engagement-and-documentation">
          <SectionTitle>Engagement and Documentation</SectionTitle>
          <SectionBody>
            <div className="flex items-center gap-[12px]">
              <span className="font-semibold text-[#132543]">Fees:</span>
              <span>{STUB.engagement.fees}</span>
            </div>
            {resumeUrl && (
              <div className="flex items-center gap-[12px]">
                <span className="font-semibold text-[#132543]">Resume:</span>
                <a
                  href={resumeUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="onepager-edit-link font-['Inter',sans-serif] text-[14px] font-medium leading-[20px] tracking-[-0.1504px] text-[#25ADE3] underline"
                >
                  {STUB.engagement.resumeLabel}
                </a>
              </div>
            )}
          </SectionBody>
        </section>
      </div>

      <div className="mt-6" data-pdf-exclude>
        <SaveBar
          showSave
          showSendEmail
          showRegenerate
          onSave={() => {}}
          onPublish={() => {}}
          onSendEmail={() => {}}
          onRegenerate={() => {}}
        />
      </div>
    </div>
  );
}
